import { useCallback } from 'react';
import {
  getOrganizationQuota,
  isOrganizationWhitelisted,
  isReadonlyBySelectedOrganization,
} from '@shared/helpers/organization.helpers';
import { useProjectsTotalGFA } from '../project';
import { StoreErrorMessage } from '../store-errors';
import {
  useOrganizationStore,
  IOrganizationsState,
} from './organization.store';
import { UserOrganization } from '@shared/models/user.model';

const selectedOrganizationSelector = (
  state: IOrganizationsState,
): IOrganizationsState['selectedOrganization'] => state.selectedOrganization;

const organizationsSelector = (
  state: IOrganizationsState,
): IOrganizationsState['organizations'] => state.organizations;

const setOrganizationsSelector = (
  state: IOrganizationsState,
): IOrganizationsState['setOrganizations'] => state.setOrganizations;

const getFullOrganizationsSelector = (
  state: IOrganizationsState,
): IOrganizationsState['fullOrganizations'] => state.fullOrganizations;

const getFullOrganizationByName = (
  state: IOrganizationsState,
  name?: UserOrganization['name'],
): UserOrganization | undefined => {
  if (name === undefined) return undefined;
  return state.fullOrganizations.find((org) => org.name === name);
};

const setSelectedOrganization = (
  state: IOrganizationsState,
): IOrganizationsState['setSelectedOrganization'] =>
  state.setSelectedOrganization;

/**
 * Get selected organization without causing rerenders
 * @param required Pass true to crash if no organization is selected
 * @returns
 */
export const getSelectedOrganization = <
  T extends ReturnType<typeof selectedOrganizationSelector>,
  P extends true | false | undefined = false,
  R = P extends true ? NonNullable<T> : T | undefined,
>(
  required?: P,
): R => {
  const organization = selectedOrganizationSelector(
    useOrganizationStore.getState(),
  );
  if (required && organization === undefined) {
    throw new Error(StoreErrorMessage.OrganizationMissing);
  }
  return organization as R;
};

/**
 * Get available organizations without causing rerenders
 * @returns
 */
export const getOrganizations = (): ReturnType<typeof organizationsSelector> =>
  organizationsSelector(useOrganizationStore.getState());

/**
 * Get selected organization name and trigger a rerender when it changes
 * @returns
 */
export const useSelectedOrganizationName = (): ReturnType<
  typeof selectedOrganizationSelector
> => useOrganizationStore(selectedOrganizationSelector);

/**
 * Set selected organization
 * @returns
 */
export const useSetSelectedOrganization = (): ReturnType<
  typeof setSelectedOrganization
> => useOrganizationStore(setSelectedOrganization);

/**
 * Get available organizations and trigger a rerender when it changes
 * @returns
 */
export const useOrganizations = (): ReturnType<typeof organizationsSelector> =>
  useOrganizationStore(organizationsSelector);

/**
 * Get available organizations and trigger a rerender when it changes
 * @returns
 */
export const useFullOrganizations = (): ReturnType<
  typeof getFullOrganizationsSelector
> => useOrganizationStore(getFullOrganizationsSelector);

export const useFullOrganizationByName = (
  name?: UserOrganization['name'],
): UserOrganization | undefined => {
  const select = useCallback(
    (state: IOrganizationsState) => getFullOrganizationByName(state, name),
    [name],
  );
  return useOrganizationStore(select);
};

export const useUserOrganization = (): UserOrganization | undefined => {
  const selectedOrganization = getSelectedOrganization();
  return useFullOrganizationByName(selectedOrganization);
};

/**
 * Set available organizations
 * @returns
 */
export const useSetOrganizations = (): ReturnType<
  typeof setOrganizationsSelector
> => useOrganizationStore(setOrganizationsSelector);

/**
 * Check if total Groos Floor Area for all the projects belonging to the
 * selected organization exceeds the organization's quota
 */
export const useOrganizationGFAQuotaIsExceeded = (): boolean => {
  const totalGFA = useProjectsTotalGFA();
  const organization = useUserOrganization();
  const quota = organization ? getOrganizationQuota(organization) : undefined;

  return quota === undefined ? false : quota === 0 || totalGFA > quota;
};

/**
 * Check if organizations includes the template organization.
 * If it does, it means that the source organizations (eg [source].organizations) is readonly,
 * unless the selected organization is the template organization.
 */
export const useIsReadonlyOrganization = () => {
  const organization = useSelectedOrganizationName();
  return useCallback(
    (organizations: string[]) =>
      isReadonlyBySelectedOrganization(organization, organizations),
    [organization],
  );
};

/**
 * Get a function to check if an item is whitelisted by the selected organization
 * @returns
 */
export const useIsSelectedOrganizationWhitelisted = (): ((
  whitelistItem: Parameters<typeof isOrganizationWhitelisted>[0],
) => boolean) => {
  const organization = useSelectedOrganizationName();
  return useCallback(
    (whitelistItem) => isOrganizationWhitelisted(whitelistItem, organization),
    [organization],
  );
};
